import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Trash2 } from 'lucide-react';
import { CartItem as CartItemType } from '@/contexts/CartContext';
import { useTranslation } from 'react-i18next';

interface CartItemProps {
  item: CartItemType;
  currency: string;
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
}

export const CartItem: React.FC<CartItemProps> = ({
  item,
  currency,
  onUpdateQuantity,
  onRemove,
}) => {
  const { t } = useTranslation();
  const { menuItem, variant, quantity, price } = item;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="flex gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm mb-3 relative overflow-hidden"
    >
      {/* Image Section - Only render if image exists */}
      {menuItem.image_url && (
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-50">
          <img
            src={menuItem.image_url}
            alt={menuItem.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content Section */}
      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex justify-between items-start gap-2">
            <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2">
              {menuItem.name}
            </h3>
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-red-500 transition-colors p-1.5 -mt-1.5 -ml-1.5 rounded-full hover:bg-red-50"
              aria-label={t('common.remove')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          
          {variant && (
            <p className="text-xs text-gray-500 mt-1 font-medium">
              {variant.size_name}
            </p>
          )}
          
          {item.notes && (
             <p className="text-xs text-gray-400 mt-1 italic line-clamp-1">
               {item.notes}
             </p>
          )}
        </div>

        <div className="flex items-end justify-between mt-2">
          <div className="font-bold text-base text-primary">
            {price.toFixed(2)} <span className="text-xs font-normal">{currency}</span>
          </div>

          {/* Quantity Controls */}
          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 h-8 shadow-sm">
            <button
              onClick={() => onUpdateQuantity(quantity - 1)}
              className="w-8 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-primary active:bg-gray-200 rounded-r-lg transition-colors"
              disabled={quantity <= 1}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-8 text-center font-bold text-sm text-gray-900">{quantity}</span>
            <button
              onClick={() => onUpdateQuantity(quantity + 1)}
              className="w-8 h-full flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-primary active:bg-gray-200 rounded-l-lg transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
